import React, { useState } from "react"
import { useDispatch } from "react-redux"
import { Typography, Stack, Card, Box, Button, Collapse } from "@mui/material"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import dayjs from "dayjs"

import ExecutionsStatus from "../../ProductionStepExecutions/ExecutionsStatus"
import IngredientsTable from "./IngredientsTable"
import ReproductionDialogForm from "./ReproductionDialogForm"
import { getPSEStatusLabel } from "../../../utils/productionStepExecution"
import { confirm } from "../../../components/react-await-dialog/react-await-dialog"
import { cancelReproduction, getReproductionNeeds } from "../../../actions/ProductionSteps/alerts"
import { COLORS, formatEmailToName } from "../../../utils"
import { PSE_STATUS, updateProductionStepExecutionStatusToDone } from "../../../actions/ProductionSteps/ProductionStepExecutions"
import { getProductionStepExecution } from "../../../parseManager/productionStepExecutions/parseProductionStepExecutionManager"
import { openAppMessageSnackBar } from "../../../actions/Utils/app"

const sx = {
  card: {
    display: "flex",
    flexDirection: "column",
    padding: "24px",
    borderRadius: "8px",
    gap: "24px",
    boxShadow: "none"
  },
  topCard: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  cardTitle: {
    fontSize: 18,
    lineHeight: 1.4,
    color: COLORS.BLACK800,
    fontWeight: 500
  },
  subtitle: {
    fontSize: 16,
    lineHeight: 1.5,
    color: COLORS.LABEL_GREY,
    fontWeight: 400
  },
  topLine: {
    display: "flex",
    justifyContent: "space-between"
  },
  dateBlock: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: "4px"
  },
  topRight: {
    fontSize: 14,
    lineHeight: 1.57,
    color: COLORS.LABEL_GREY,
    fontWeight: 400
  },
  date: {
    fontSize: 16,
    lineHeight: 1.5,
    color: COLORS.BLACK800,
    fontWeight: 400
  },
  mainContent: {
    padding: "16px",
    borderRadius: "4px"
  },
  label: {
    fontSize: 14,
    lineHeight: 1.57,
    color: COLORS.LABEL_GREY,
    fontWeight: 400
  },
  value: {
    fontSize: 16,
    lineHeight: 1.5,
    color: COLORS.BLACK800,
    fontWeight: 400
  },
  ingredientsLabel: {
    fontSize: 16,
    lineHeight: 1.5,
    color: COLORS.BLACK800,
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "4px"
  },
  bottomBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "end"
  },
  actionButton: {
    textTransform: "uppercase",
    borderRadius: "4px",
    padding: "15px 24px",
    fontSize: 15,
    fontWeight: 500,
    lineHeight: 1,
    boxShadow: "none"
  },
  reprodCancelationText: {
    fontFamily: "Roboto",
    fontSize: "16px",
    lineHeight: "22px",
    fontWeight: 400,
    padding: 0,
    color: COLORS.BLACK_500,
    "& strong": {
      fontWeight: 700
    }
  },
  reprodCancelationPaper: {
    "& .MuiDialogContent-root": {
      width: "500px",
      padding: "24px 24px 16px 24px"
    },
    "& .MuiDialogActions-root": {
      padding: "0 24px 24px 24px",
      "& button": {
        color: COLORS.PRIMARY_COLOR,
        height: "40px",
        padding: "10px 20px",
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "14px"
      }
    }
  }
}

const ReproductionNeedsCard = ({ reproductionNeed }) => {
  const dispatch = useDispatch()

  const [showIngredients, setShowIngredients] = useState(false)
  const [isOpenReprodDialog, setIsOpenReprodDialog] = useState(false)

  const packagingDates = reproductionNeed.packagingDates?.map(date => dayjs(date).format("DD/MM/YYYY")) || []
  const formattedIngredients = reproductionNeed.ingredients
    ? reproductionNeed.ingredients.map(ing => ({
      name: ing.name || "Ingrédient inconnu",
      nextDelivery: ing.nextDelivery,
      available: ing.availableWeight ?? null,
      needed: ing.neededWeight ?? null,
      isShort: (ing.availableWeight ?? null) <= (ing.neededWeight ?? null)
    }))
    : [{ name: "Aucun ingrédient renseigné" }]

  const isPending = reproductionNeed.reproductionStatus === "PENDING_APPROVAL"
  const isTodo = reproductionNeed.reproductionStatus === "TODO"
  const isInprogress = reproductionNeed.pseStatus === "IN_PROGRESS"

  const toggleDialog = () => setIsOpenReprodDialog(!isOpenReprodDialog)

  const getCardStyle = () =>
    isTodo || isPending
      ? { backgroundColor: COLORS.BLUE_50, border: "none" }
      : { backgroundColor: COLORS.WHITE, border: `1px solid ${COLORS.BLACK_400}` }

  const getMainContentStyle = () =>
    isTodo || isPending
      ? { backgroundColor: COLORS.WHITE }
      : { backgroundColor: COLORS.BACKGROUND_LIGHT_GREY }

  const handleConfirmPSEToDone = () => {
    return confirm({
      message: (
        <Typography sx={sx.reprodCancelationText}>
          Confirmez-vous vouloir continuer sans faire de reprod ?<br />
          Cette action est irréversible.
        </Typography>
      ),
      confirmText: "Confirmer",
      paper: sx.reprodCancelationPaper,
      valueSupplier: async () => {
        const productionStepExecution = await getProductionStepExecution(reproductionNeed.originalPSEId, true, [])
        const formattedValues = {
          status: productionStepExecution.isSectionLastStep ? PSE_STATUS.toTest : PSE_STATUS.done
        }
        await dispatch(updateProductionStepExecutionStatusToDone(reproductionNeed.originalPSEId, formattedValues))
        dispatch(getReproductionNeeds())
        dispatch(openAppMessageSnackBar("Mise à jour effectuée avec succès."))
      }
    })
  }

  const handleCancelReproduction = () => {
    return confirm({
      message: (
        <Typography sx={sx.reprodCancelationText}>
          Vous avez choisi d'annuler la reprod pour la section{" "}
          <strong>{reproductionNeed.sectionName} ({reproductionNeed.uniqueCode})</strong>
          . Confirmez-vous cette action ?
        </Typography>
      ),
      confirmText: "Confirmer",
      paper: sx.reprodCancelationPaper,
      valueSupplier: async () => { await dispatch(cancelReproduction({ reprodId: reproductionNeed.objectId })) }
    })
  }

  return (
    <>
      <ReproductionDialogForm
        open={isOpenReprodDialog}
        reproductionNeed={reproductionNeed}
        onClose={toggleDialog}
      />
      <Card sx={{ ...sx.card, ...getCardStyle() }}>
        {/* -------- Section name, unique code, recipe name and packaging date(s) -------- */}
        <Box sx={sx.topCard}>
          <Box sx={sx.topLine}>
            <Typography sx={sx.cardTitle}>{reproductionNeed.sectionName}</Typography>
            {packagingDates.length > 0 && (
              <Box sx={sx.dateBlock}>
                <Typography sx={sx.topRight}>Barquettage le :</Typography>
                <Stack spacing={0.5}>
                  {packagingDates.map((date, index) => (
                    <Typography key={index} sx={sx.date}>
                      {date}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
          <Typography sx={sx.subtitle}>
            {reproductionNeed.uniqueCode} - {reproductionNeed.recipeName}
          </Typography>
        </Box>

        {/* -------- Main content: reproduction info -------- */}
        <Box sx={{ ...sx.mainContent, ...getMainContentStyle() }}>
          <Stack direction="row" spacing={3}>
            <Stack spacing={1}>
              <Typography sx={sx.label}>{isPending ? "Quantité manquante/prévue" : "Quantité en reprod"}</Typography>
              <Typography sx={sx.value}>
                {isPending
                  ? `${Math.round(reproductionNeed.reproductionNetWeight * 100) / 100}/${Math.round(reproductionNeed.expectedNetWeight * 100) / 100} kg`
                  : `${Math.round(reproductionNeed.reproductionNetWeight * 100) / 100} kg`}
              </Typography>
            </Stack>
            {!isPending && (
              <Stack spacing={1}>
                <Typography sx={sx.label}>Statut de la reprod</Typography>
                <ExecutionsStatus
                  statusValue={reproductionNeed.reproductionStatus}
                  statusName={getPSEStatusLabel(reproductionNeed.reproductionStatus)}
                />
              </Stack>
            )}
            <Stack spacing={1}>
              <Typography sx={sx.label}>Atelier</Typography>
              <Typography sx={sx.value}>{reproductionNeed.kitchenArea}</Typography>
            </Stack>
            <Stack spacing={1}>
              <Typography sx={sx.label}>Utilisateur</Typography>
              <Typography sx={sx.value}>{formatEmailToName(reproductionNeed.reproductionAuthor)}</Typography>
            </Stack>
          </Stack>
        </Box>

        {/* -------- Ingredients information -------- */}
        {(isTodo || isPending) && (
          <>
            <Box sx={sx.bottomBar}>
              <Typography
                sx={sx.ingredientsLabel}
                onClick={() => setShowIngredients(prev => !prev)}
              >
                Disponibilité des ingrédients
                {showIngredients ? (
                  <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />
                ) : (
                  <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                )}
              </Typography>
              <Stack direction="row" spacing={2}>
                {(!isTodo && isInprogress) && <Button
                  variant="outlined"
                  sx={sx.actionButton}
                  onClick={handleConfirmPSEToDone}
                >
                  CONTINUER SANS REPROD
                </Button>}
                <Button
                  variant="contained"
                  onClick={isTodo ? handleCancelReproduction : () => setIsOpenReprodDialog(true)}
                  sx={{
                    ...sx.actionButton,
                    backgroundColor: isTodo
                      ? COLORS.RED_ERROR
                      : COLORS.PRIMARY_COLOR
                  }}
                >
                  {isTodo ? "Annuler la reprod" : "Faire une reprod"}
                </Button>
              </Stack>
            </Box>

            <Collapse in={showIngredients} timeout="auto" unmountOnExit>
              <IngredientsTable
                ingredients={formattedIngredients || []}
              />
            </Collapse>
          </>
        )}
      </Card>
    </>
  )
}

export default ReproductionNeedsCard